import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { v4 } from 'uuid'
import BackupIcon from '@mui/icons-material/Backup'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import DownloadIcon from '@mui/icons-material/Download'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import ReplayIcon from '@mui/icons-material/Replay'
import RestoreIcon from '@mui/icons-material/Restore'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'
import StopIcon from '@mui/icons-material/Stop'
import UpgradeIcon from '@mui/icons-material/Upgrade'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { formatFileSize } from 'common/api/v1/helpers'
import {
    blocksHostingActions,
    canDeleteHosting,
    canRequestHostingAction,
    canRetryHosting,
    canRestoreHostingBackupInPlace,
    hostingManagementUrl,
    hostingUpgradeTargets,
    isNotableHostingOperation,
    isRetainedHostingBackup,
    managedHostingBackupPurposeLabels,
    managedHostingLocationLabel,
    managedHostingMessageSeverity,
    managedHostingOperationLabels,
    managedHostingOperationStageLabel,
    managedHostingOperationStatusLabels,
    managedHostingOperationStatusTone,
    managedHostingPowerLabel,
    managedHostingRoleLabel,
    managedHostingStatusLabels,
    managedHostingStatusTone,
    recoverableDeletionStatuses,
} from 'common-billing-server/managedHosting'
import {
    ManagedHostingStatus,
    type ManagedHostingAdminDeployment,
    type ManagedHostingBackup,
    type ManagedHostingBackupDownload,
    type ManagedHostingLocation,
    type ManagedHostingOperation,
    type NewManagedHostingOperation,
} from 'common-billing-server/types'
import { Banner } from 'common-ui/components/Banner'
import DataSet from 'common-ui/components/DataSet'
import { ActionMenu } from 'common-ui/components/Table/ActionMenu'
import { Table, type TableConfig } from 'common-ui/components/Table/Table'
import { useBillingApi } from '../../api/billing/billing-context'
import { formatDateTime } from '../../format-date'
import { makeEditCustomerPath, makeManagedHostingDetailsPath } from '../../routes/path'
import { useConfirmationDialog } from '../common/ConfirmationDialog'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import Wrapper from '../common/Wrapper'

const POLL_INTERVAL_MS = 5000

type OperationRequest = Omit<NewManagedHostingOperation, 'idempotencyKey'>

const optionalDateTime = (value?: Date) => (value ? formatDateTime(value) : '—')

/** Follows a backup's signed link; storage sends the file as an attachment, so the page stays open. */
const startBackupDownload = ({ url, filename }: ManagedHostingBackupDownload) => {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
}

const StatusChip = ({ label, tone }: { label: string; tone: ReturnType<typeof managedHostingStatusTone> }) => (
    <Chip size="small" label={label} color={tone} />
)

export const ManagedHostingDetailsPage = () => {
    const { deploymentId } = useParams()
    return <DeploymentDetails key={deploymentId} id={deploymentId ?? ''} />
}

const DeploymentDetails = ({ id }: { id: string }) => {
    const api = useBillingApi()
    const showConfirmation = useConfirmationDialog()
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()
    const [deployment, setDeployment] = useState<ManagedHostingAdminDeployment>()
    const [nodes, setNodes] = useState<ManagedHostingAdminDeployment[]>([])
    const [backups, setBackups] = useState<ManagedHostingBackup[]>([])
    const [operations, setOperations] = useState<ManagedHostingOperation[]>([])
    const [locations, setLocations] = useState<ManagedHostingLocation[]>([])
    const [loading, setLoading] = useState(true)
    const [loadFailed, setLoadFailed] = useState(false)
    const [reloadCount, setReloadCount] = useState(0)
    const [submitting, setSubmitting] = useState(false)
    const [upgradeOpen, setUpgradeOpen] = useState(false)
    const [upgradeId, setUpgradeId] = useState('')
    const [notice, setNotice] = useState<string>()
    // A request that failed keeps its key, so retrying it cannot queue the same work twice.
    const requestKeys = useRef(new Map<string, string>())

    useEffect(() => {
        let active = true
        let inFlight = false
        const load = async () => {
            if (inFlight) return
            inFlight = true
            try {
                const [nextDeployment, nextOperations] = await Promise.all([
                    api.getManagedHostingDeployment(id),
                    api.listManagedHostingDeploymentOperations(id),
                ])
                // Only a Core hosts nodes and keeps backups.
                const [nextNodes, nextBackups] =
                    nextDeployment.kind === 'core'
                        ? await Promise.all([
                              api
                                  .listManagedHostingDeployments({ filter: { controlPlaneId: id }, limit: 100 })
                                  .then(({ items }) => items),
                              api.listManagedHostingDeploymentBackups(id),
                          ])
                        : [[], []]
                if (!active) return
                setDeployment(nextDeployment)
                setBackups(nextBackups)
                setOperations(nextOperations)
                setNodes(nextNodes)
                setLoadFailed(false)
                removeFailedOperation('load-managed-hosting')
            } catch (error) {
                if (!active) return
                setLoadFailed(true)
                addFailedOperation({
                    id: 'load-managed-hosting',
                    message: 'The deployment could not be loaded. It may no longer exist.',
                    error: error as Error,
                    retryFn: () => setReloadCount((count) => count + 1),
                })
            } finally {
                inFlight = false
                if (active) setLoading(false)
            }
        }
        void load()
        const timer = setInterval(() => void load(), POLL_INTERVAL_MS)
        return () => {
            active = false
            clearInterval(timer)
        }
    }, [api, id, reloadCount, addFailedOperation, removeFailedOperation])

    useEffect(() => {
        let active = true
        api.getManagedHostingCatalog()
            .then((catalog) => {
                if (active) setLocations(catalog.locations)
            })
            .catch((error: unknown) => {
                if (!active) return
                addFailedOperation({
                    id: 'load-managed-hosting-catalog',
                    message: 'The available upgrade releases could not be loaded.',
                    error: error as Error,
                })
            })
        return () => {
            active = false
        }
    }, [api, addFailedOperation])

    const reload = () => setReloadCount((count) => count + 1)
    const activeOperation = operations.find(blocksHostingActions)
    const activeStage = activeOperation ? managedHostingOperationStageLabel(activeOperation) : undefined
    const busy = submitting || loading || loadFailed || !!activeOperation
    const upgrades = deployment ? hostingUpgradeTargets(deployment, locations) : []

    const request = async (input: OperationRequest, label: string) => {
        const requestId = JSON.stringify(input)
        const idempotencyKey = requestKeys.current.get(requestId) ?? v4()
        requestKeys.current.set(requestId, idempotencyKey)
        setSubmitting(true)
        removeFailedOperation('managed-hosting-operation')
        try {
            await api.operationsManagedHostingDeployment(id, { ...input, idempotencyKey })
            requestKeys.current.delete(requestId)
            setNotice(`${label} queued for ${deployment?.name}.`)
            reload()
            return true
        } catch (error) {
            addFailedOperation({
                id: 'managed-hosting-operation',
                message: `${label} could not be queued. Check the deployment status and try again.`,
                error: error as Error,
                retryFn: () => void request(input, label),
            })
            return false
        } finally {
            setSubmitting(false)
        }
    }

    const confirmRequest = (
        input: OperationRequest,
        label: string,
        message: ReactNode,
        { confirmText = label, destructive = false }: { confirmText?: string; destructive?: boolean } = {}
    ) =>
        showConfirmation(() => void request(input, label), message, {
            ok: { text: confirmText, color: destructive ? 'error' : 'primary' },
        })

    const remove = async () => {
        setSubmitting(true)
        removeFailedOperation('managed-hosting-delete')
        try {
            await api.deleteManagedHostingDeployment(id)
            setNotice(`Deletion queued for ${deployment?.name}.`)
            reload()
        } catch (error) {
            addFailedOperation({
                id: 'managed-hosting-delete',
                message: 'The deployment could not be deleted. Check its status and try again.',
                error: error as Error,
                retryFn: () => void remove(),
            })
        } finally {
            setSubmitting(false)
        }
    }

    const confirmDelete = (target: ManagedHostingAdminDeployment) => {
        const recoverable = recoverableDeletionStatuses.includes(target.status)
        showConfirmation(
            () => void remove(),
            <Stack spacing={1}>
                <Typography sx={{ fontWeight: 500 }}>Delete {target.name}?</Typography>
                <Typography>
                    {recoverable
                        ? `Stop this hosted ${managedHostingRoleLabel(target).toLowerCase()} for ${target.customer.name} and retain its resources for seven days, so it can be restored. After that period the resources are permanently removed.`
                        : `Cancel this incomplete launch for ${target.customer.name} and permanently remove its partial cloud resources. There is no running service to restore.`}
                </Typography>
                <Typography sx={{ overflowWrap: 'anywhere' }}>{target.hostname}</Typography>
                {target.kind === 'core' && (
                    <Alert severity="info">
                        {recoverable
                            ? 'This also stops the control plane’s hosted nodes. Restoring the control plane restores them.'
                            : 'This also removes hosted nodes belonging to this incomplete control plane.'}
                    </Alert>
                )}
            </Stack>,
            { ok: { text: 'Delete deployment', color: 'error' } }
        )
    }

    const lifecycleActions = (target: ManagedHostingAdminDeployment) => {
        const can = (action: NewManagedHostingOperation['action']) => canRequestHostingAction(target, action)
        const options = []
        if (can('upgrade') && upgrades.length > 0)
            options.push({ label: 'Upgrade', icon: <UpgradeIcon />, onClick: () => setUpgradeOpen(true) })
        if (can('start'))
            options.push({
                label: 'Start',
                icon: <PlayArrowIcon />,
                onClick: () => confirmRequest({ action: 'start' }, 'Start', `Start ${target.name}.`),
            })
        if (can('stop'))
            options.push({
                label: 'Stop',
                icon: <StopIcon />,
                onClick: () =>
                    confirmRequest(
                        { action: 'stop' },
                        'Stop',
                        `Stop ${target.name}. Streaming through it is interrupted.`
                    ),
            })
        if (can('set-power-policy')) {
            const automatic = target.powerPolicy === 'automatic'
            options.push({
                label: automatic ? 'Use manual power' : 'Use automatic power',
                icon: <PowerSettingsNewIcon />,
                onClick: () =>
                    confirmRequest(
                        { action: 'set-power-policy', powerPolicy: automatic ? 'manual' : 'automatic' },
                        'Power policy change',
                        automatic
                            ? 'Keep this node under manual power control.'
                            : 'Stop this node after 15 minutes without enabled stream work and start it within a minute when work appears.'
                    ),
            })
        }
        if (can('reconcile'))
            options.push({
                label: 'Run health check',
                icon: <MonitorHeartIcon />,
                onClick: () =>
                    confirmRequest(
                        { action: 'reconcile' },
                        'Health check',
                        'Check this deployment and reconcile its cloud and control plane state.'
                    ),
            })
        if (canRetryHosting(target, operations))
            options.push({
                label: 'Retry failed work',
                icon: <ReplayIcon />,
                onClick: () =>
                    confirmRequest(
                        { action: 'retry' },
                        'Retry',
                        'Retry the failed deployment work using its recorded configuration.'
                    ),
            })
        if (can('restore') && !!target.recoverableUntil && new Date(target.recoverableUntil).getTime() > Date.now())
            options.push({
                label: 'Restore',
                icon: <RestoreIcon />,
                onClick: () =>
                    confirmRequest(
                        { action: 'restore' },
                        'Restore',
                        `Restart ${target.name} and its retained hosted nodes and restore service.`
                    ),
            })
        if (canDeleteHosting(target))
            options.push({
                label: target.status === ManagedHostingStatus.deleteFailed ? 'Retry delete' : 'Delete',
                icon: <DeleteIcon />,
                isDestructive: true,
                onClick: () => confirmDelete(target),
            })
        if (can('purge'))
            options.push({
                label: 'Delete permanently',
                icon: <DeleteForeverIcon />,
                isDestructive: true,
                onClick: () =>
                    confirmRequest(
                        { action: 'purge' },
                        'Permanent deletion',
                        `Permanently remove ${target.name} for ${target.customer.name} and its retained cloud resources, including hosted nodes belonging to this control plane. This cannot be undone.`,
                        { destructive: true }
                    ),
            })
        return options.map((option) => ({ ...option, disabled: busy }))
    }

    const nodeColumns: TableConfig<ManagedHostingAdminDeployment> = [
        {
            title: 'Node',
            getValue: (node) => <Link to={makeManagedHostingDetailsPath(node.id)}>{node.name}</Link>,
            props: { noTypography: true },
        },
        { title: 'Type', getValue: (node) => managedHostingRoleLabel(node) },
        { title: 'Location', getValue: (node) => managedHostingLocationLabel(node.location) },
        { title: 'Release', getValue: (node) => node.location.version },
        {
            title: 'Status',
            getValue: (node) => (
                <StatusChip
                    label={managedHostingStatusLabels[node.status]}
                    tone={managedHostingStatusTone(node.status)}
                />
            ),
            props: { noTypography: true },
        },
    ]

    const confirmBackup = (target: ManagedHostingAdminDeployment) =>
        confirmRequest(
            { action: 'backup' },
            'Backup',
            `Back up the configuration of ${target.name} now, without interrupting service. The backup is kept for 30 days.`,
            { confirmText: 'Back up' }
        )

    const confirmRestore = (target: ManagedHostingAdminDeployment, backup: ManagedHostingBackup) =>
        confirmRequest(
            { action: 'restore-backup', backupId: backup.id },
            'Restore from backup',
            <Stack spacing={1}>
                <Typography sx={{ fontWeight: 500 }}>Restore {target.name} from backup?</Typography>
                <Typography>
                    The configuration from the backup taken {formatDateTime(backup.createdAt)} UTC replaces the current
                    one, and changes made since then are lost. Network Manager restarts, and streams on the Edge
                    Connects reconnect for a moment while their configuration is applied again.
                </Typography>
                <Alert severity="info">
                    Active hosted Edge Connects enrolled after the backup was taken reconnect automatically on their
                    existing VMs. Stopped nodes start temporarily to reconnect. Recently deleted nodes absent from the
                    backup must be permanently deleted and deployed again.
                </Alert>
            </Stack>,
            { confirmText: 'Restore' }
        )

    const downloadBackup = async (backup: ManagedHostingBackup) => {
        removeFailedOperation('managed-hosting-download')
        try {
            startBackupDownload(await api.getManagedHostingDeploymentBackupsDownload(id, backup.id))
        } catch (error) {
            addFailedOperation({
                id: 'managed-hosting-download',
                message: 'The backup could not be downloaded. Please try again.',
                error: error as Error,
                retryFn: () => void downloadBackup(backup),
            })
        }
    }

    // A download needs neither a running Core nor an idle one.
    const backupActions = (target: ManagedHostingAdminDeployment, backup: ManagedHostingBackup) =>
        isRetainedHostingBackup(backup)
            ? [
                  { label: 'Download', icon: <DownloadIcon />, onClick: () => void downloadBackup(backup) },
                  ...(canRequestHostingAction(target, 'restore-backup') &&
                  canRestoreHostingBackupInPlace(target, backup)
                      ? [
                            {
                                label: 'Restore',
                                icon: <SettingsBackupRestoreIcon />,
                                onClick: () => confirmRestore(target, backup),
                                disabled: busy,
                            },
                        ]
                      : []),
              ]
            : []

    const backupColumns: TableConfig<ManagedHostingBackup> = [
        { title: 'Taken (UTC)', getValue: (backup) => formatDateTime(backup.createdAt) },
        { title: 'Purpose', getValue: (backup) => managedHostingBackupPurposeLabels[backup.purpose] },
        { title: 'Release', getValue: (backup) => backup.version },
        { title: 'Size', getValue: (backup) => formatFileSize(backup.sizeBytes) },
        { title: 'Expires (UTC)', getValue: (backup) => formatDateTime(backup.expiresAt) },
        {
            getValue: (backup) => {
                const options = deployment ? backupActions(deployment, backup) : []
                return options.length > 0 ? <ActionMenu data-test-id={`backup-${backup.id}`} options={options} /> : null
            },
            props: { noTypography: true, align: 'right' },
            headerProps: { 'aria-label': 'Backup actions' },
        },
    ]

    const operationColumns: TableConfig<ManagedHostingOperation> = [
        { title: 'Operation', getValue: (operation) => managedHostingOperationLabels[operation.action] },
        {
            title: 'Status',
            getValue: (operation) => (
                <>
                    <StatusChip
                        label={managedHostingOperationStatusLabels[operation.status]}
                        tone={managedHostingOperationStatusTone(operation.status)}
                    />
                    {operation.error && (
                        <Typography variant="body2" color="error" sx={{ overflowWrap: 'anywhere' }}>
                            {operation.error}
                        </Typography>
                    )}
                </>
            ),
            props: { noTypography: true },
        },
        { title: 'Stage', getValue: (operation) => managedHostingOperationStageLabel(operation) ?? '—' },
        { title: 'Started (UTC)', getValue: (operation) => formatDateTime(operation.createdAt) },
        { title: 'Updated (UTC)', getValue: (operation) => formatDateTime(operation.updatedAt) },
    ]

    const managementUrl =
        deployment && [ManagedHostingStatus.ready, ManagedHostingStatus.degraded].includes(deployment.status)
            ? hostingManagementUrl(deployment)
            : undefined
    const actions = deployment ? lifecycleActions(deployment) : []
    const selectedUpgradeId = upgrades.some((location) => location.id === upgradeId) ? upgradeId : upgrades[0]?.id

    return (
        <Wrapper
            title={deployment?.name ?? 'Deployment details'}
            actions={
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    {deployment && managementUrl && (
                        <Button
                            variant="outlined"
                            startIcon={<OpenInNewIcon />}
                            href={managementUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {deployment.kind === 'core' ? 'Open Network Manager' : 'Open appliance UI'}
                        </Button>
                    )}
                    {actions.length > 0 && <ActionMenu data-test-id="deployment-actions" options={actions} />}
                </Stack>
            }
        >
            {loading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
                    <CircularProgress aria-label="Loading deployment details" />
                </Box>
            ) : deployment ? (
                <Stack spacing={2}>
                    <Paper sx={{ p: 2 }}>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mb: 2, alignItems: 'center', justifyContent: 'space-between' }}
                        >
                            <Typography variant="h2">Deployment</Typography>
                            <StatusChip
                                label={managedHostingStatusLabels[deployment.status]}
                                tone={managedHostingStatusTone(deployment.status)}
                            />
                        </Stack>
                        <Stack spacing={2}>
                            {deployment.error && (
                                <Alert severity={managedHostingMessageSeverity(deployment.status)}>
                                    {deployment.error}
                                </Alert>
                            )}
                            {deployment.status === ManagedHostingStatus.recoverable && (
                                <Alert severity="warning">
                                    Deleted and retained until {optionalDateTime(deployment.recoverableUntil)} UTC.
                                    Restore it before then to resume service.
                                </Alert>
                            )}
                            {activeOperation && (
                                <Alert severity="info">
                                    {managedHostingOperationLabels[activeOperation.action]} is in progress.
                                    {activeStage ? ` Current step: ${activeStage}.` : null} Other actions are available
                                    when it finishes.
                                </Alert>
                            )}
                            <DataSet
                                values={{
                                    Type: managedHostingRoleLabel(deployment),
                                    Customer: (
                                        <Link to={makeEditCustomerPath(deployment.customer.id)}>
                                            {deployment.customer.name}
                                        </Link>
                                    ),
                                    Location: managedHostingLocationLabel(deployment.location),
                                    Release: deployment.location.version,
                                    Address: deployment.hostname,
                                    'Public IP': deployment.publicIp ?? '—',
                                    'Private IP': deployment.privateIp ?? '—',
                                    ...(deployment.kind === 'connect'
                                        ? {
                                              'Control plane': deployment.controlPlaneId ? (
                                                  <Link to={makeManagedHostingDetailsPath(deployment.controlPlaneId)}>
                                                      {deployment.controlPlaneHostname ?? deployment.controlPlaneId}
                                                  </Link>
                                              ) : (
                                                  '—'
                                              ),
                                              Power: managedHostingPowerLabel(deployment),
                                          }
                                        : {}),
                                    'Last health check (UTC)': optionalDateTime(deployment.lastReconciledAt),
                                    'Created (UTC)': formatDateTime(deployment.createdAt),
                                    ...(deployment.recoverableUntil
                                        ? { 'Recoverable until (UTC)': formatDateTime(deployment.recoverableUntil) }
                                        : {}),
                                    'Deployment ID': deployment.id,
                                }}
                            />
                        </Stack>
                    </Paper>
                    {deployment.kind === 'core' && (
                        <Paper sx={{ p: 2, minWidth: 0 }}>
                            <Typography variant="h2" sx={{ mb: 2 }}>
                                Hosted nodes
                            </Typography>
                            <Table
                                id="managed-hosting-nodes"
                                config={nodeColumns}
                                data={nodes}
                                props={{ 'aria-label': 'Hosted nodes' }}
                                emptyMessageComponent={<Alert severity="info">No hosted nodes.</Alert>}
                            />
                        </Paper>
                    )}
                    {deployment.kind === 'core' && (
                        <Paper sx={{ p: 2, minWidth: 0 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mb: 1, alignItems: 'center', justifyContent: 'space-between' }}
                            >
                                <Typography variant="h2">Backups</Typography>
                                {canRequestHostingAction(deployment, 'backup') && (
                                    <Button
                                        startIcon={<BackupIcon />}
                                        disabled={busy}
                                        onClick={() => confirmBackup(deployment)}
                                    >
                                        Back up now
                                    </Button>
                                )}
                            </Stack>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                The control plane backs up its configuration every six hours without interrupting
                                service. Backups are kept for 30 days and can be downloaded, also while the control
                                plane is down. A downloaded backup can be restored on a new control plane from Network
                                Manager’s backup settings. Edge Connects need no backup: their configuration lives on
                                the control plane.
                            </Typography>
                            <Table
                                id="managed-hosting-backups"
                                config={backupColumns}
                                data={backups}
                                props={{ 'aria-label': 'Backups' }}
                                emptyMessageComponent={<Alert severity="info">No backups yet.</Alert>}
                            />
                        </Paper>
                    )}
                    <Paper sx={{ p: 2, minWidth: 0 }}>
                        <Typography variant="h2" sx={{ mb: 2 }}>
                            Operations
                        </Typography>
                        <Table
                            id="managed-hosting-operations"
                            config={operationColumns}
                            data={operations.filter(isNotableHostingOperation)}
                            props={{ 'aria-label': 'Operations' }}
                            emptyMessageComponent={<Alert severity="info">No lifecycle operations yet.</Alert>}
                        />
                    </Paper>
                </Stack>
            ) : null}
            {errorBanner}
            {notice && <Banner id="notice" isOpen message={notice} onClose={() => setNotice(undefined)} />}
            <Dialog open={upgradeOpen} onClose={submitting ? undefined : () => setUpgradeOpen(false)} fullWidth>
                <DialogTitle>Upgrade {deployment?.name}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {deployment?.kind === 'core'
                            ? 'Back up the control plane configuration, then install the release in place without stopping the instance. Network Manager is unavailable while it restarts, and streams on the Edge Connects reconnect for a moment when it returns. A failed upgrade is reported and nothing is restored automatically; restore the backup taken before the upgrade in place while the control plane still runs, or download it.'
                            : 'Install the release in place without stopping the instance. Streams through the node are interrupted while it restarts.'}
                    </DialogContentText>
                    <TextField
                        select
                        fullWidth
                        label="Release"
                        value={selectedUpgradeId ?? ''}
                        onChange={(event) => setUpgradeId(event.target.value)}
                        disabled={submitting}
                    >
                        {upgrades.map((location) => (
                            <MenuItem key={location.id} value={location.id}>
                                {location.version}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpgradeOpen(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={busy || !selectedUpgradeId}
                        onClick={() => {
                            if (!selectedUpgradeId) return
                            void request({ action: 'upgrade', locationId: selectedUpgradeId }, 'Upgrade').then(
                                (queued) => {
                                    if (queued) setUpgradeOpen(false)
                                }
                            )
                        }}
                    >
                        Upgrade
                    </Button>
                </DialogActions>
            </Dialog>
        </Wrapper>
    )
}
